import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { CheckCircle2, AlertCircle, KeyRound } from 'lucide-react';
import Logo from '../components/Logo';

// Single page used for: forgot, reset (with token), and confirm-change (with token)
export const ForgotPassword = ({ scope = 'admin' }) => {
  // scope = 'admin' or 'customer'
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const path = scope === 'admin' ? '/admin/password/forgot' : '/customers/password/forgot';
      const r = await api.post(path, { email });
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-10 fade-in">
      <div className="w-full max-w-md bg-white border border-neutral-200 rounded-xl p-8 shadow-sm">
        <div className="flex justify-center mb-4"><Logo size="sm" /></div>
        <div className="flex items-center gap-2 mb-4">
          <KeyRound className="text-[#d62828]" size={20} />
          <h1 className="text-2xl font-black brand-text">Password dimenticata</h1>
        </div>
        <p className="text-sm text-neutral-600 mb-4">Inserisci la tua email. Riceverai un link per resettare la password.</p>
        {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}
        {result ? (
          <div className="p-4 bg-green-50 border border-green-200 rounded text-green-800 space-y-2">
            <p className="flex items-start gap-2"><CheckCircle2 size={18} className="mt-0.5 flex-shrink-0" /> {result.message}</p>
            {result.confirm_token && (
              <div className="mt-2 p-3 bg-white border border-amber-300 rounded">
                <p className="text-xs text-amber-700 font-bold mb-2">✉️ EMAIL SIMULATA</p>
                <p className="text-xs text-neutral-600 mb-2">In produzione il link arriverebbe via email a <strong>{result.sent_to}</strong>. Per test, clicca qui:</p>
                <Link to={`/reset-password?token=${result.confirm_token}&scope=${scope}`} className="inline-block px-4 py-2 bg-[#d62828] text-white text-sm font-bold rounded hover:bg-[#b41f1f]">
                  Apri link di reset →
                </Link>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@dominio.com" className="w-full px-4 py-2.5 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30" />
            <button type="submit" disabled={submitting} className="w-full px-5 py-3 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f] disabled:opacity-60">
              {submitting ? 'Invio...' : 'Invia link'}
            </button>
          </form>
        )}
        <p className="text-xs text-center mt-4">
          <Link to={scope === 'admin' ? '/admin' : '/cliente/login'} className="text-neutral-500 hover:text-[#d62828]">← Torna al login</Link>
        </p>
      </div>
    </div>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const scope = params.get('scope') || 'admin';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password minima 6 caratteri'); return; }
    if (password !== confirm) { setError('Le password non coincidono'); return; }
    setSubmitting(true);
    try {
      const path = scope === 'admin' ? '/admin/password/reset' : '/customers/password/reset';
      await api.post(path, { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate(scope === 'admin' ? '/admin' : '/cliente/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore reset');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-10 fade-in">
      <div className="w-full max-w-md bg-white border border-neutral-200 rounded-xl p-8 shadow-sm">
        <h1 className="text-2xl font-black brand-text mb-4">Imposta nuova password</h1>
        {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}
        {success ? (
          <div className="p-4 bg-green-50 border border-green-200 rounded text-green-800 flex items-center gap-2"><CheckCircle2 size={18} /> Password aggiornata. Reindirizzamento...</div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-sm font-bold text-neutral-700 mb-1">Nuova password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-2.5 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30" />
            </div>
            <div>
              <label className="block text-sm font-bold text-neutral-700 mb-1">Conferma password</label>
              <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="w-full px-4 py-2.5 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30" />
            </div>
            <button type="submit" disabled={submitting || !token} className="w-full px-5 py-3 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f] disabled:opacity-60">
              {submitting ? 'Salvataggio...' : 'Aggiorna password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export const ConfirmChangePassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const scope = params.get('scope') || 'admin';
  const [status, setStatus] = useState('pending');
  const [error, setError] = useState('');

  const confirm = async () => {
    setStatus('loading');
    setError('');
    try {
      const path = scope === 'admin' ? '/admin/password/change-confirm' : '/customers/password/change-confirm';
      await api.post(`${path}?token=${encodeURIComponent(token)}`);
      setStatus('done');
      setTimeout(() => navigate(scope === 'admin' ? '/admin/dashboard' : '/cliente/profilo'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore conferma');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-10 fade-in">
      <div className="w-full max-w-md bg-white border border-neutral-200 rounded-xl p-8 shadow-sm text-center">
        <h1 className="text-2xl font-black brand-text mb-4">Conferma cambio password</h1>
        {status === 'pending' && (
          <>
            <p className="text-sm text-neutral-600 mb-4">Conferma il cambio password cliccando il pulsante.</p>
            <button onClick={confirm} className="px-6 py-3 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f]">Conferma</button>
          </>
        )}
        {status === 'loading' && <p>Conferma in corso...</p>}
        {status === 'done' && <p className="text-green-700 flex items-center justify-center gap-2"><CheckCircle2 size={18} /> Password aggiornata!</p>}
        {status === 'error' && <p className="text-red-700 flex items-center justify-center gap-2"><AlertCircle size={18} /> {error}</p>}
      </div>
    </div>
  );
};
